<template>
  <chatflow-page>
    <div v-if="loading" class="text-center py-12">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <div v-else class="cf-settings-wrap shadow-xl cf-card" elevation="2">
      <div class="cf-settings-header px-4 pt-4">
        <div>
          <h1>Ayarlar</h1>
          <p>Kanal bağlantıları ve yapay zeka / intent sağlayıcıları</p>
        </div>
        <v-btn color="primary" depressed :loading="saving" @click="save">
          Tümünü kaydet
        </v-btn>
      </div>

      <div class="px-4">
        <div class="cf-settings-primary">
          <button
            type="button"
            class="cf-settings-primary__btn"
            :class="{ 'is-active': mainTab === 'channels' }"
            @click="mainTab = 'channels'"
          >
            <v-icon small>mdi-access-point</v-icon>
            Kanallar
          </button>
          <button
            type="button"
            class="cf-settings-primary__btn"
            :class="{ 'is-active': mainTab === 'ai' }"
            @click="mainTab = 'ai'"
          >
            <v-icon small>mdi-brain</v-icon>
            Yapay zeka
          </button>
        </div>

        <div v-if="mainTab === 'channels'" class="cf-settings-sub">
          <button
            v-for="t in channelTabs"
            :key="t.id"
            type="button"
            class="cf-settings-sub__btn"
            :class="{ 'is-active': channelTab === t.id }"
            @click="channelTab = t.id"
          >
            <v-icon x-small>{{ t.icon }}</v-icon>
            {{ t.label }}
          </button>
        </div>

        <div v-else class="cf-settings-sub">
          <button
            v-for="t in aiTabs"
            :key="t.id"
            type="button"
            class="cf-settings-sub__btn"
            :class="{ 'is-active': aiTab === t.id }"
            @click="aiTab = t.id"
          >
            <v-icon x-small>{{ t.icon }}</v-icon>
            {{ t.label }}
          </button>
        </div>

        <div class="cf-settings-panel">
          <div class="cf-settings-panel__head">
            <v-icon small color="white">{{ panelIcon }}</v-icon>
            {{ panelTitle }}
          </div>

          <div class="cf-settings-panel__body">
            <!-- Kanallar: E-posta -->
            <template v-if="mainTab === 'channels' && channelTab === 'mail'">
              <div class="cf-settings-notice">
                <div class="cf-settings-notice__label">NOT</div>
                <v-text-field
                  v-model="settings.mail.from"
                  label="İsteğe bağlı not / gönderen"
                  outlined
                  dense
                  hide-details
                  placeholder="SMTP gönderen adresi"
                />
              </div>
              <v-switch
                v-model="settings.mail.enabled"
                label="E-posta kanalı etkin"
                class="mb-4"
              />
              <div class="cf-settings-form-grid">
                <v-text-field
                  v-model="settings.mail.host"
                  label="SMTP host"
                  outlined
                  dense
                />
                <v-text-field
                  v-model="settings.mail.port"
                  label="Port"
                  type="number"
                  outlined
                  dense
                />
                <v-text-field
                  v-model="settings.mail.user"
                  label="Kullanıcı"
                  outlined
                  dense
                />
                <v-text-field
                  v-model="settings.mail.password"
                  label="Şifre"
                  type="password"
                  autocomplete="new-password"
                  outlined
                  dense
                  hint="Boş bırakırsanız mevcut şifre korunur"
                  persistent-hint
                />
              </div>
            </template>

            <!-- Kanallar: Telegram -->
            <template v-if="mainTab === 'channels' && channelTab === 'telegram'">
              <div class="cf-settings-notice">
                <div class="cf-settings-notice__label">NOT</div>
                <v-textarea
                  v-model="settings.connectors.telegram.notes"
                  label="İsteğe bağlı not"
                  outlined
                  dense
                  rows="2"
                  auto-grow
                  hide-details
                />
              </div>
              <v-switch
                v-model="settings.connectors.telegram.enabled"
                label="Telegram etkin"
                class="mb-4"
              />

              <p class="cf-settings-form-title">Bağlı botlar</p>
              <div v-if="!telegramChannels.length" class="cf-tg-empty mb-4">
                <v-icon color="grey lighten-1" class="mr-2">mdi-robot-confused-outline</v-icon>
                Henüz bağlı Telegram botu yok. Aşağıdan yeni bot ekleyebilirsiniz.
              </div>
              <div v-else class="cf-tg-list mb-4">
                <v-card
                  v-for="ch in telegramChannels"
                  :key="ch.id"
                  outlined
                  class="cf-tg-card mb-3"
                >
                  <v-card-text class="py-3">
                    <div class="cf-tg-card__head">
                      <div>
                        <div class="cf-tg-card__name">{{ ch.name }}</div>
                        <div class="cf-tg-card__bot">
                          <v-icon x-small class="mr-1">mdi-send</v-icon>
                          {{
                            ch.credentials && ch.credentials.botUsername
                              ? `@${ch.credentials.botUsername}`
                              : "Bot kullanıcı adı yok"
                          }}
                        </div>
                      </div>
                      <div class="cf-tg-card__chips">
                        <v-chip x-small :color="ch.active ? 'success' : 'grey'" text-color="white" class="mr-1">
                          {{ ch.active ? "Aktif" : "Pasif" }}
                        </v-chip>
                        <v-chip v-if="tgStatus[ch.id]" x-small outlined color="primary">
                          {{ telegramModeLabel(tgStatus[ch.id]) }}
                        </v-chip>
                      </div>
                    </div>
                    <v-row dense class="mt-2 cf-tg-card__meta">
                      <v-col cols="12" sm="6">
                        <span class="cf-tg-meta-label">Token</span>
                        <code class="cf-tg-meta-value">{{
                          (ch.credentials && ch.credentials.botTokenHint) || "•••••••• (kayıtlı)"
                        }}</code>
                        <div class="caption grey--text mt-1">Güvenlik için tam token gösterilmez</div>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <span class="cf-tg-meta-label">Giriş akışı</span>
                        <div class="cf-tg-meta-value">{{ flowName(ch.flowId) }}</div>
                      </v-col>
                      <v-col v-if="webhookUrl(ch)" cols="12">
                        <span class="cf-tg-meta-label">Webhook</span>
                        <div class="cf-tg-meta-value cf-tg-webhook-url">{{ webhookUrl(ch) }}</div>
                      </v-col>
                    </v-row>
                    <div class="cf-tg-card__actions mt-2">
                      <v-btn
                        small
                        text
                        color="primary"
                        :loading="tgWebhookLoading === ch.id"
                        @click="registerWebhook(ch.id)"
                      >
                        Webhook yenile
                      </v-btn>
                      <v-btn small text color="error" @click="removeTelegram(ch)">Botu kaldır</v-btn>
                    </div>
                  </v-card-text>
                </v-card>
              </div>

              <p class="cf-settings-form-title">Yeni bot bağla</p>
              <div class="cf-settings-form-grid">
                <v-text-field
                  v-model="tgForm.name"
                  label="Bot adı"
                  outlined
                  dense
                  hide-details
                />
                <v-text-field
                  v-model="tgForm.botToken"
                  label="Bot token"
                  type="password"
                  autocomplete="new-password"
                  outlined
                  dense
                  hide-details
                />
                <v-select
                  v-model="tgForm.flowId"
                  :items="flows"
                  item-text="name"
                  item-value="id"
                  label="Giriş akışı"
                  outlined
                  dense
                  clearable
                  hide-details
                />
              </div>
              <div class="cf-settings-actions">
                <v-btn color="primary" depressed :loading="tgSaving" @click="connectTelegram">
                  Telegram bot bağla
                </v-btn>
              </div>
            </template>

            <!-- Kanallar: WhatsApp -->
            <template v-if="mainTab === 'channels' && channelTab === 'whatsapp'">
              <div class="cf-settings-notice">
                <div class="cf-settings-notice__label">NOT</div>
                <v-text-field
                  v-model="settings.connectors.whatsapp.verifyToken"
                  label="İsteğe bağlı not / verify token"
                  outlined
                  dense
                  hide-details
                />
              </div>
              <v-switch
                v-model="settings.connectors.whatsapp.enabled"
                label="WhatsApp etkin"
                class="mb-4"
              />
              <div class="cf-settings-form-grid">
                <v-text-field
                  v-model="settings.connectors.whatsapp.phoneNumberId"
                  label="Phone number ID"
                  outlined
                  dense
                />
                <v-text-field
                  v-model="settings.connectors.whatsapp.accessToken"
                  label="Access token"
                  type="password"
                  autocomplete="new-password"
                  outlined
                  dense
                />
                <v-text-field
                  v-model="settings.connectors.whatsapp.apiUrl"
                  label="API URL"
                  outlined
                  dense
                />
              </div>
            </template>

            <!-- Kanallar: Teams -->
            <template v-if="mainTab === 'channels' && channelTab === 'teams'">
              <v-switch
                v-model="settings.connectors.teams.enabled"
                label="Microsoft Teams etkin"
                class="mb-4"
              />
              <div class="cf-settings-form-grid">
                <v-text-field
                  v-model="settings.connectors.teams.appId"
                  label="App ID"
                  outlined
                  dense
                />
                <v-text-field
                  v-model="settings.connectors.teams.tenantId"
                  label="Tenant ID"
                  outlined
                  dense
                />
                <v-text-field
                  v-model="settings.connectors.teams.appPassword"
                  label="App password"
                  type="password"
                  autocomplete="new-password"
                  outlined
                  dense
                />
                <v-text-field
                  v-model="settings.connectors.teams.messagingEndpoint"
                  label="Messaging endpoint"
                  outlined
                  dense
                />
              </div>
            </template>

            <!-- Kanallar: Rocket.Chat -->
            <template v-if="mainTab === 'channels' && channelTab === 'rocketchat'">
              <v-switch
                v-model="settings.connectors.rocketchat.enabled"
                label="Rocket.Chat etkin"
                class="mb-4"
              />
              <div class="cf-settings-form-grid">
                <v-text-field
                  v-model="settings.connectors.rocketchat.serverUrl"
                  label="Sunucu URL"
                  outlined
                  dense
                />
                <v-text-field
                  v-model="settings.connectors.rocketchat.userId"
                  label="User ID"
                  outlined
                  dense
                />
                <v-text-field
                  v-model="settings.connectors.rocketchat.authToken"
                  label="Auth token"
                  type="password"
                  autocomplete="new-password"
                  outlined
                  dense
                />
                <v-text-field
                  v-model="settings.connectors.rocketchat.webhookToken"
                  label="Webhook token"
                  outlined
                  dense
                />
              </div>
            </template>

            <!-- AI: Intent -->
            <template v-if="mainTab === 'ai' && aiTab === 'keyword'">
              <div class="cf-settings-notice">
                <div class="cf-settings-notice__label">NOT</div>
                <v-textarea
                  v-model="settings.ai.notes"
                  label="İsteğe bağlı not"
                  outlined
                  dense
                  rows="2"
                  auto-grow
                  hide-details
                />
              </div>
              <v-select
                v-model="settings.ai.activeProvider"
                :items="aiProviders"
                item-text="label"
                item-value="value"
                label="Aktif intent sağlayıcısı"
                outlined
                dense
                class="cf-settings-form-grid"
                style="max-width: 420px"
              />
              <p class="caption text--secondary mt-2 mb-0">
                Akıştaki Intent adımları bu sağlayıcıyı kullanır.
              </p>
            </template>

            <!-- AI: Gemini -->
            <template v-if="mainTab === 'ai' && aiTab === 'gemini'">
              <div class="cf-settings-form-grid">
                <v-text-field
                  v-model="settings.ai.gemini.apiKey"
                  label="API anahtarı"
                  type="password"
                  autocomplete="new-password"
                  outlined
                  dense
                  hint="Boş bırakırsanız mevcut anahtar korunur (***)"
                  persistent-hint
                />
                <v-text-field
                  v-model="settings.ai.gemini.model"
                  label="Model"
                  outlined
                  dense
                  hide-details
                />
              </div>
            </template>

            <!-- AI: OpenAI -->
            <template v-if="mainTab === 'ai' && aiTab === 'openai'">
              <div class="cf-settings-form-grid">
                <v-text-field
                  v-model="settings.ai.openai.apiKey"
                  label="API anahtarı"
                  type="password"
                  autocomplete="new-password"
                  outlined
                  dense
                />
                <v-text-field
                  v-model="settings.ai.openai.model"
                  label="Model"
                  outlined
                  dense
                />
                <v-text-field
                  v-model="settings.ai.openai.organizationId"
                  label="Organization ID"
                  outlined
                  dense
                />
              </div>
            </template>

            <!-- AI: Dialogflow -->
            <template v-if="mainTab === 'ai' && aiTab === 'dialogflow'">
              <div class="cf-settings-form-grid">
                <v-text-field
                  v-model="settings.ai.dialogflow.projectId"
                  label="Project ID"
                  outlined
                  dense
                />
                <v-text-field
                  v-model="settings.ai.dialogflow.agentId"
                  label="Agent ID"
                  outlined
                  dense
                />
                <v-text-field
                  v-model="settings.ai.dialogflow.location"
                  label="Location"
                  outlined
                  dense
                />
                <v-text-field
                  v-model="settings.ai.dialogflow.languageCode"
                  label="Dil kodu"
                  outlined
                  dense
                />
                <v-textarea
                  v-model="settings.ai.dialogflow.credentialsJson"
                  label="Service account JSON"
                  outlined
                  dense
                  rows="4"
                  class="cf-settings-full-row"
                />
              </div>
            </template>
          </div>
        </div>
      </div>
      <div class="pb-4" />
    </div>

    <v-snackbar v-model="snack" :color="snackColor">{{ snackText }}</v-snackbar>
  </chatflow-page>
</template>

<script>
import ChatflowApi from "../api";
import ChatflowAuthMixin from "../mixins/auth";
import ChatflowPage from "../components/ChatflowPage.vue";
import "../styles/settings.css";

const emptySettings = () => ({
  mail: {
    enabled: false,
    host: "",
    port: 587,
    secure: false,
    user: "",
    password: "",
    from: "",
  },
  connectors: {
    telegram: { enabled: true, notes: "" },
    whatsapp: {
      enabled: false,
      apiUrl: "https://graph.facebook.com/v18.0",
      phoneNumberId: "",
      accessToken: "",
      verifyToken: "",
      appSecret: "",
    },
    teams: {
      enabled: false,
      appId: "",
      appPassword: "",
      tenantId: "",
      messagingEndpoint: "",
    },
    rocketchat: {
      enabled: false,
      serverUrl: "",
      userId: "",
      authToken: "",
      webhookToken: "",
    },
  },
  ai: {
    activeProvider: "keyword",
    notes: "",
    gemini: { apiKey: "", model: "gemini-2.0-flash" },
    openai: { apiKey: "", model: "gpt-4o-mini", organizationId: "" },
    dialogflow: {
      projectId: "",
      location: "europe-west1",
      agentId: "",
      languageCode: "tr",
      credentialsJson: "",
    },
  },
});

function deepMergeSettings(base, data) {
  const out = JSON.parse(JSON.stringify(base));
  const merge = (t, s) => {
    if (!s || typeof s !== "object") return;
    Object.keys(s).forEach((k) => {
      if (s[k] && typeof s[k] === "object" && !Array.isArray(s[k])) {
        t[k] = t[k] || {};
        merge(t[k], s[k]);
      } else if (s[k] !== undefined) {
        t[k] = s[k];
      }
    });
  };
  merge(out, data);
  return out;
}

export default {
  components: { ChatflowPage },
  mixins: [ChatflowAuthMixin("Chatflow Settings")],
  data: () => ({
    loading: false,
    saving: false,
    tgSaving: false,
    mainTab: "channels",
    channelTab: "telegram",
    aiTab: "keyword",
    settings: emptySettings(),
    flows: [],
    tgForm: { name: "", botToken: "", flowId: null },
    channelTabs: [
      { id: "mail", label: "E-posta", icon: "mdi-email" },
      { id: "telegram", label: "Telegram", icon: "mdi-send" },
      { id: "whatsapp", label: "WhatsApp", icon: "mdi-whatsapp" },
      { id: "teams", label: "Teams", icon: "mdi-microsoft-teams" },
      { id: "rocketchat", label: "Rocket.Chat", icon: "mdi-rocket-launch" },
    ],
    aiTabs: [
      { id: "keyword", label: "Intent", icon: "mdi-key" },
      { id: "gemini", label: "Gemini", icon: "mdi-google" },
      { id: "openai", label: "OpenAI", icon: "mdi-robot" },
      { id: "dialogflow", label: "Dialogflow", icon: "mdi-message-processing" },
    ],
    aiProviders: [
      { value: "keyword", label: "Anahtar kelime (varsayılan)" },
      { value: "gemini", label: "Google Gemini" },
      { value: "openai", label: "OpenAI" },
      { value: "dialogflow", label: "Dialogflow" },
    ],
    snack: false,
    snackText: "",
    snackColor: "success",
    channels: [],
    tgStatus: {},
    tgWebhookLoading: null,
  }),
  computed: {
    telegramChannels() {
      return (this.channels || []).filter((c) => c.type === "telegram");
    },
    panelTitle() {
      if (this.mainTab === "channels") {
        const t = this.channelTabs.find((x) => x.id === this.channelTab);
        return t ? `${t.label} — Bağlantı ayarları` : "Kanal";
      }
      const t = this.aiTabs.find((x) => x.id === this.aiTab);
      return t ? `${t.label} — Yapay zeka` : "Yapay zeka";
    },
    panelIcon() {
      if (this.mainTab === "channels") {
        const t = this.channelTabs.find((x) => x.id === this.channelTab);
        return (t && t.icon) || "mdi-cog";
      }
      const t = this.aiTabs.find((x) => x.id === this.aiTab);
      return (t && t.icon) || "mdi-brain";
    },
  },
  created() {
    this.load();
  },
  methods: {
    flowName(flowId) {
      if (!flowId) return "— (atanmamış)";
      const f = this.flows.find((x) => x.id === flowId);
      return f ? f.name : flowId;
    },
    telegramModeLabel(status) {
      if (!status) return "";
      if (status.telegramMode === "polling") return "Polling";
      const wh = status.webhook;
      if (wh && wh.url) return "Webhook aktif";
      return "Webhook";
    },
    webhookUrl(ch) {
      const st = this.tgStatus[ch.id];
      return (st && st.webhook && st.webhook.url) || "";
    },
    async loadChannelsAndStatus() {
      this.channels = (await ChatflowApi.getChannels()) || [];
      const ids = this.telegramChannels.map((c) => c.id);
      const pairs = await Promise.all(
        ids.map(async (id) => {
          try {
            const st = await ChatflowApi.getTelegramStatus(id);
            return [id, st];
          } catch {
            return [id, null];
          }
        })
      );
      this.tgStatus = Object.fromEntries(pairs);
    },
    async load() {
      this.loading = true;
      try {
        const [data, flows] = await Promise.all([
          ChatflowApi.getSettings(),
          ChatflowApi.getFlows(),
        ]);
        this.settings = deepMergeSettings(emptySettings(), data);
        this.flows = flows || [];
        await this.loadChannelsAndStatus();
      } catch (e) {
        this.notify(e.response?.data?.message || "Ayarlar yüklenemedi", "error");
      }
      this.loading = false;
    },
    async save() {
      this.saving = true;
      try {
        await ChatflowApi.updateSettings(this.settings);
        this.notify("Ayarlar kaydedildi", "success");
        await this.load();
      } catch (e) {
        this.notify(e.response?.data?.message || "Kayıt başarısız", "error");
      }
      this.saving = false;
    },
    async connectTelegram() {
      if (!this.tgForm.name || !this.tgForm.botToken) {
        this.notify("Bot adı ve token gerekli", "warning");
        return;
      }
      this.tgSaving = true;
      try {
        const ch = await ChatflowApi.createTelegramChannel({
          name: this.tgForm.name.trim(),
          botToken: this.tgForm.botToken.trim(),
          flowId: this.tgForm.flowId || null,
        });
        await ChatflowApi.registerTelegramWebhook(ch.id);
        this.tgForm = { name: "", botToken: "", flowId: null };
        await this.loadChannelsAndStatus();
        this.notify("Telegram bot bağlandı", "success");
      } catch (e) {
        this.notify(e.response?.data?.message || "Bağlantı başarısız", "error");
      }
      this.tgSaving = false;
    },
    async registerWebhook(channelId) {
      this.tgWebhookLoading = channelId;
      try {
        const reg = await ChatflowApi.registerTelegramWebhook(channelId);
        await this.loadChannelsAndStatus();
        this.notify(reg.webhookUrl ? `Webhook: ${reg.webhookUrl}` : "Webhook güncellendi", "success");
      } catch (e) {
        this.notify(e.response?.data?.message || "Webhook kaydı başarısız", "error");
      }
      this.tgWebhookLoading = null;
    },
    async removeTelegram(ch) {
      if (!confirm(`"${ch.name}" Telegram botunu kaldırmak istiyor musunuz?`)) return;
      try {
        await ChatflowApi.deleteChannel(ch.id);
        await this.loadChannelsAndStatus();
        this.notify("Bot kaldırıldı", "success");
      } catch (e) {
        this.notify(e.response?.data?.message || "Silinemedi", "error");
      }
    },
    notify(text, color = "success") {
      this.snackText = text;
      this.snackColor = color;
      this.snack = true;
    },
  },
};
</script>

<style scoped>
.cf-card {
  border-radius: 8px;
  background: #fff;
}

.cf-settings-full-row {
  grid-column: 1 / -1;
}

.cf-settings-form-grid .cf-settings-full-row {
  max-width: 100%;
}

.cf-tg-empty {
  display: flex;
  align-items: center;
  padding: 12px 14px;
  border-radius: 8px;
  background: #f5f5f5;
  color: #757575;
  font-size: 0.9rem;
}

.cf-tg-card {
  border-color: rgba(102, 63, 92, 0.2) !important;
}

.cf-tg-card__head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
}

.cf-tg-card__name {
  font-weight: 600;
  font-size: 1rem;
  color: #1a1a2e;
}

.cf-tg-card__bot {
  font-size: 0.85rem;
  color: #663f5c;
  margin-top: 2px;
}

.cf-tg-meta-label {
  display: block;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #9e9e9e;
  margin-bottom: 2px;
}

.cf-tg-meta-value {
  font-size: 0.9rem;
  color: #424242;
  word-break: break-all;
}

.cf-tg-webhook-url {
  font-family: monospace;
  font-size: 0.8rem;
}
</style>
